import React from "react";
import { Navigate, Link } from "react-router-dom";
import { CreditCard, LogOut, User, Mail, Phone, Gift, Copy, CheckCircle2, Calendar } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useCustomerAuth } from "../context/CustomerAuthContext";

function formatDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch (e) {
    return d;
  }
}

export default function CustomerDashboard() {
  const { customer, loading, logout } = useCustomerAuth();

  if (loading || customer === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="w-10 h-10 border-4 border-brand/30 border-t-brand rounded-full animate-spin" />
      </div>
    );
  }
  if (!customer) return <Navigate to="/area-clienti/login" replace />;

  const card = customer.card;
  const shareUrl = card?.referral_code ? `${window.location.origin}/?ref=${card.referral_code}` : "";

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <section className="bg-surface border-b border-line">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10 flex items-center justify-between gap-4">
          <div>
            <div className="label-eyebrow text-brand-dark">Area Clienti</div>
            <h1 className="mt-1 font-heading font-extrabold text-3xl sm:text-4xl text-navy" data-testid="customer-welcome">
              Ciao, {customer.name?.split(" ")[0] || "Cliente"}!
            </h1>
          </div>
          <button
            onClick={logout}
            data-testid="customer-logout"
            className="inline-flex items-center gap-2 text-sm font-bold text-navy border border-line hover:border-brand hover:text-brand-dark rounded-full px-4 py-2.5 transition-colors bg-white"
          >
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </section>

      <section className="max-w-4xl mx-auto w-full px-5 sm:px-8 py-10 flex-1 space-y-8">
        {/* Fidelity card */}
        {card ? (
          <div className="relative overflow-hidden rounded-2xl bg-navy text-white p-8 soft-shadow-lg" data-testid="customer-card">
            <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full bg-brand/20" />
            <div className="absolute -right-4 top-16 w-24 h-24 rounded-full bg-brand/20" />
            <div className="relative flex items-start justify-between">
              <div>
                <div className="label-eyebrow text-brand">Carta Fedeltà</div>
                <div className="mt-1 font-heading font-bold text-xl">{card.holder_name || customer.name}</div>
                <div className="mt-1 font-mono text-sm text-white/70" data-testid="customer-card-number">{card.card_number}</div>
              </div>
              <CreditCard className="w-8 h-8 text-brand" />
            </div>
            <div className="relative mt-8">
              <div className="text-white/70 text-sm">Saldo punti</div>
              <div className="font-heading font-extrabold text-5xl text-brand" data-testid="customer-points">{card.points}</div>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-line bg-white p-8 text-center text-navy-soft">
            Nessuna carta associata al tuo account.
          </div>
        )}

        {/* Anagrafica */}
        <div className="rounded-2xl border border-line bg-white p-6 soft-shadow" data-testid="customer-profile">
          <h2 className="font-heading font-bold text-lg text-navy mb-4">I tuoi dati</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-brand-light text-brand-darker flex items-center justify-center shrink-0"><User className="w-5 h-5" /></span>
              <div><div className="text-xs text-navy-soft">Nome</div><div className="font-semibold text-navy">{customer.name || "—"}</div></div>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-brand-light text-brand-darker flex items-center justify-center shrink-0"><Mail className="w-5 h-5" /></span>
              <div><div className="text-xs text-navy-soft">Email</div><div className="font-semibold text-navy break-all">{customer.email}</div></div>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-brand-light text-brand-darker flex items-center justify-center shrink-0"><Phone className="w-5 h-5" /></span>
              <div><div className="text-xs text-navy-soft">Telefono</div><div className="font-semibold text-navy">{customer.phone || "—"}</div></div>
            </div>
          </div>
        </div>

        {/* Member-get-member */}
        {shareUrl && (
          <div className="rounded-2xl border border-brand/30 bg-brand-light/50 p-6" data-testid="customer-share">
            <div className="flex items-center gap-2 text-brand-darker font-bold">
              <Gift className="w-5 h-5" /> Presenta un amico e guadagna punti extra
            </div>
            <p className="mt-2 text-sm text-navy-soft">
              Condividi il tuo link personale: quando un amico attiva un servizio, ricevi punti extra sulla tua carta.
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <input
                readOnly
                value={shareUrl}
                onFocus={(e) => e.target.select()}
                data-testid="customer-share-url"
                className="flex-1 bg-white border border-line rounded-full px-4 py-3 text-xs font-mono text-navy outline-none"
              />
              <button
                onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copiato!"); }}
                data-testid="customer-share-copy"
                className="inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-white font-bold px-5 py-3 rounded-full transition-colors shrink-0"
              >
                <Copy className="w-4 h-4" /> Copia link
              </button>
            </div>
          </div>
        )}

        {/* Services / entries */}
        <div>
          <h2 className="font-heading font-bold text-2xl text-navy">Servizi attivati</h2>
          <p className="text-sm text-navy-soft mt-1">I servizi che ti hanno fatto accumulare punti.</p>
          {card && card.entries && card.entries.length > 0 ? (
            <div className="mt-5 space-y-3" data-testid="customer-entries">
              {card.entries.map((e) => (
                <div key={e.id} className="bg-white rounded-2xl border border-line p-5 flex items-center justify-between gap-4 soft-shadow">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-11 h-11 rounded-xl bg-brand-light text-brand-darker flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-navy truncate">{e.service_name}</div>
                      <div className="flex items-center gap-3 text-xs text-navy-soft mt-0.5">
                        {e.category && <span>{e.category}</span>}
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(e.date)}</span>
                      </div>
                      {e.note && <div className="text-xs text-navy-soft mt-1">{e.note}</div>}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-heading font-extrabold text-xl text-brand-dark">+{e.points}</div>
                    <div className="text-[11px] text-navy-soft uppercase tracking-wide">punti</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-5 bg-surface rounded-2xl border border-line py-12 text-center text-navy-soft text-sm">
              Nessun servizio registrato al momento. Contattaci su WhatsApp per attivare la tua prima offerta!{" "}
              <Link to="/" className="text-brand-dark font-bold hover:underline">Scopri le offerte</Link>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
